import { useEffect, useMemo, useState } from "react";
import { getInlineLoader } from "../Helpers";
import { IoChevronBack, IoChevronForward } from "react-icons/io5";
import { FaMountain, FaUmbrellaBeach, FaSeedling, FaDirections } from "react-icons/fa";
import wild_plains from "../assets/wild_plains.jpg";
import banner1 from "../assets/kili.jpg";
import zanzibar1 from "../assets/zanzibar1.jpg";
import bananaridge from "../assets/banner2.jpg";

const sliderItems = [
  {
    title: "Serengeti Luxury Safaris",
    text: "Private guides and 5-star camps that put guests on the migration in comfort.",
    image: wild_plains,
    meta: "Wildlife",
  },
  {
    title: "Kilimanjaro & Mountain Treks",
    text: "Custom acclimatisation plans with medical-grade logistics and local support.",
    image: banner1,
    meta: "Trekking",
  },
  {
    title: "Zanzibar Coastline Escapes",
    text: "Stone Town culture, spice tours, and boutique villas along turquoise shores.",
    image: zanzibar1,
    meta: "Coastal",
  },
  {
    title: "Tailor-made Cultural Discovery",
    text: "Community walks, curated artisans, and conservation-led day itineraries.",
    image: bananaridge,
    meta: "Culture",
  },
];

const highlightCards = [
  {
    icon: <FaDirections size={28} />,
    title: "Guides with local roots",
    text: "Expert teams born and raised in Northern Tanzania keep every experience authentic.",
  },
  {
    icon: <FaMountain size={28} />,
    title: "Safety-first journeys",
    text: "Emergency-ready vehicles, satellite comms, and supported camps for peace of mind.",
  },
  {
    icon: <FaUmbrellaBeach size={28} />,
    title: "Bush-to-beach rhythm",
    text: "Seamless transitions between migration safaris, mountain climbs, and Zanzibar relaxation.",
  },
  {
    icon: <FaSeedling size={28} />,
    title: "Community partnerships",
    text: "We share revenue with Maasai rangers, women-led lodges, and conservation initiatives.",
  },
];

export default function AboutUsContent() {
  const [ready, setReady] = useState(false);
  const [activeSlide, setActiveSlide] = useState(0);

  const slideTimeout = useMemo(() => 5000, []);

  useEffect(() => {
    const timer = setTimeout(() => setReady(true), 100);
    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    if (!ready) return;
    const interval = setInterval(() => {
      setActiveSlide((prev) => (prev + 1) % sliderItems.length);
    }, slideTimeout);
    return () => clearInterval(interval);
  }, [ready, slideTimeout]);

  if (!ready) {
    return (
      <div className="container py-5 text-center">
        {getInlineLoader()}
      </div>
    );
  }

  const goNext = () => setActiveSlide((prev) => (prev + 1) % sliderItems.length);
  const goPrev = () =>
    setActiveSlide((prev) => (prev - 1 + sliderItems.length) % sliderItems.length);

  return (
    <section className="MainBodySection aboutus-wrap">
      <div className="container">
        <div className="aboutus-center mb-4">
          <div className="section-heading-pro section-heading-pro--center">
            <span className="section-heading-pro__rail" />
            <h2 className="section-heading-pro__title font-bold">About Kwetu Africa Adventures</h2>
            <p className="section-heading-pro__meta">Centered storytelling · Authentic experiences</p>
          </div>
          <p className="mt-3" style={{ maxWidth: 640, margin: "0 auto", color: "#4b5a6d" }}>
            We design professionally curated safaris, treks, and coastal journeys that feel turned
            on for every traveler. Elevated imagery, transparent logistics, and respectful community
            engagement keep our team grounded and modern.
          </p>
        </div>

        <div className="aboutc-slider-shell mb-4">
          <div className="aboutc-slider-track">
            {sliderItems.map((item, index) => (
              <div
                key={item.title}
                className="aboutc-slide"
                style={{
                  opacity: index === activeSlide ? 1 : 0,
                  transform: index === activeSlide ? "scale(1)" : "scale(1.02)",
                }}
              >
                <img src={item.image} alt={item.title} />
                <div className="aboutc-slide-overlay" />
                <div className="aboutc-slide-caption">
                  <p className="section-heading-pro__meta mb-1">{item.meta}</p>
                  <h3 className="font-bold mb-1">{item.title}</h3>
                  <p className="mb-0" style={{ color: "#f0f3ff" }}>
                    {item.text}
                  </p>
                </div>
              </div>
            ))}
          </div>
          <div className="aboutc-slider-controls">
            <button className="aboutc-arrow" type="button" onClick={goPrev} aria-label="Previous">
              <IoChevronBack size={22} />
            </button>
            <button className="aboutc-arrow" type="button" onClick={goNext} aria-label="Next">
              <IoChevronForward size={22} />
            </button>
          </div>
        </div>

        <div className="aboutc-cap-grid">
          {highlightCards.map((card) => (
            <div key={card.title} className="aboutc-panel">
              <div className="aboutc-panel-head">
                <span className="kwetu-highlight-card__icon">{card.icon}</span>
                <h4 className="aboutc-panel-title">{card.title}</h4>
              </div>
              <p className="aboutc-panel-text text-muted">{card.text}</p>
            </div>
          ))}
        </div>

        <div className="aboutus-stats-grid mt-4">
          <div className="aboutus-stats-item">
            <strong>98%</strong>
            <span>Traveler satisfaction</span>
            <p>Respectful guides and polished itineraries earn returns and referrals.</p>
          </div>
          <div className="aboutus-stats-item">
            <strong>400+</strong>
            <span>Private departures</span>
            <p>From Serengeti migration to Zanzibar retreats, every journey is bespoke.</p>
          </div>
          <div className="aboutus-stats-item">
            <strong>24/7</strong>
            <span>Whatsapp desk</span>
            <p>Live updates, confirmations, and post-travel follow-ups with a local team.</p>
          </div>
        </div>
      </div>
    </section>
  );
}
